import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { FeedSkeleton } from './components/ui/FeedSkeleton';
import { lazyWithRetry } from './lib/lazyWithRetry';
import { DebugProvider } from './view-system/debug/DebugContext';
import type { ActionDefinition } from '@shared/schemas/instant-action-definition';

// Lazy-loaded shared modals. PreferencesDialog is NOT mounted here — the
// nav-rail Preferences item renders on every authenticated surface, so the
// dialog mounts once in AuthenticatedLayout (AppRouter.tsx), not per-layout.
const ActionTriggerModal = lazyWithRetry(() => import('./view-system/ActionTriggerModal'));

/**
 * Outlet context type for V4 route components.
 * Route children use `useOutletContext<AppOutletContext>()` to access shared state.
 */
export type AppOutletContext = {
  onSelectAction: (action: ActionDefinition | null) => void;
};

/**
 * AppLayout — V4 app shell for path-segment routing.
 *
 * Owns shared chrome (ErrorBoundary, DebugProvider, Suspense)
 * and renders child routes via <Outlet />. Route components receive
 * `onSelectAction` through outlet context instead of props.
 *
 * This replaces the view-switching logic that used to live in V4App — the first
 * path segment IS the view, so no appView param is needed.
 */
export function AppLayout() {
  const [selectedAction, setSelectedAction] = useState<ActionDefinition | null>(null);

  const outletContext: AppOutletContext = {
    onSelectAction: setSelectedAction,
  };

  return (
    <ErrorBoundary>
      <DebugProvider>
        <React.Suspense fallback={<FeedSkeleton />}>
          <Outlet context={outletContext} />
        </React.Suspense>
        <React.Suspense fallback={null}>
          <ActionTriggerModal
            definition={selectedAction}
            open={!!selectedAction}
            onClose={() => setSelectedAction(null)}
          />
        </React.Suspense>
      </DebugProvider>
    </ErrorBoundary>
  );
}
